/**
 * iMikel-style enrollment detail page with 4 main tabs:
 *   - Veranstaltungen (course + termin + lehrkraft)
 *   - Belegungen     (teilnehmer + zahlungspflichtige + kontakte)
 *   - Abrechnung     (beträge + summen breakdown)
 *   - Übersicht      (sibling enrollments + quick actions)
 *
 * Single API call to /enrollments/{id}/full pulls all related data; the
 * Abrechnung tab fetches its own per-month breakdown from /abrechnung.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import {
  Calendar, Users, Receipt, List, Loader2, ArrowLeft,
  User as UserIcon, MapPin, GraduationCap, CreditCard, Clock, Plus, Trash2, AlertTriangle,
} from 'lucide-react';
import api from '@/lib/axios';
import Layout from '@/components/layout/Layout';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import MultiPayerEditor from './MultiPayerEditor';
import { formatDate, formatTime, formatWeekdayShort, formatWeekdayTimeRange, addMinutesToTime, courseDisplayName, formatDeNumber } from '@/lib/utils';
import { courseTypeLabel, eventLabel, rhythmLabel, statusLabel } from '@/lib/deLabels';

function HistoryPanel({ enrollmentId }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    if (!enrollmentId) return;
    setLoading(true);
    api.get(`/enrollments/${enrollmentId}/history`)
      .then((r) => setItems(r.data))
      .catch(() => setItems([]))
      .finally(() => setLoading(false));
  }, [enrollmentId]);
  return (
    <div data-testid="history-panel">
      <div className="flex items-center gap-2 mb-3">
        <Clock className="w-4 h-4 text-brand-red" />
        <h4 className="font-semibold text-slate-800 text-sm">Historie</h4>
        <span className="text-xs text-slate-400">({items.length} Einträge)</span>
      </div>
      {loading ? (
        <Loader2 className="w-4 h-4 animate-spin text-slate-400" />
      ) : items.length === 0 ? (
        <p className="text-xs text-slate-400 italic">Noch keine Audit-Einträge — werden automatisch bei jeder Änderung erstellt.</p>
      ) : (
        <ol className="space-y-2 max-h-72 overflow-y-auto pr-1">
          {items.map((h) => (
            <li key={h.id} className="border-l-2 border-brand-red/20 pl-3 py-1.5">
              <div className="flex items-baseline gap-2 text-xs">
                <span className="text-slate-400">
                  {new Date(h.created_at).toLocaleString('de-DE', { dateStyle: 'short', timeStyle: 'short' })}
                </span>
                <span className="px-1.5 py-0.5 bg-brand-red/10 text-brand-red rounded text-[10px]">
                  {eventLabel(h.event_type)}
                </span>
                {h.actor_name && (
                  <span className="text-slate-500">· {h.actor_name}</span>
                )}
              </div>
              <div className="text-sm text-slate-700 mt-0.5">{h.summary}</div>
              {h.notes && <div className="text-xs text-slate-500 mt-0.5 italic">{h.notes}</div>}
            </li>
          ))}
        </ol>
      )}
    </div>
  );
}

const Field = ({ label, children }) => (
  <div className="grid grid-cols-3 items-baseline gap-2 text-sm py-1.5 border-b border-slate-100 last:border-b-0">
    <span className="text-slate-500 text-xs uppercase tracking-wide">{label}</span>
    <span className="col-span-2 font-medium text-slate-800">
      {children || <span className="text-slate-300">—</span>}
    </span>
  </div>
);

const SectionTitle = ({ children, color = 'brand' }) => (
  <h3 className={`text-xs font-bold uppercase tracking-wider mb-3 text-${color}-700`}>
    {children}
  </h3>
);

function fmtMoney(v) {
  if (v == null) return '—';
  return new Intl.NumberFormat('de-DE', {
    style: 'currency', currency: 'EUR',
  }).format(Number(v));
}
function fmtDate(s) {
  if (!s) return '—';
  return formatDate(s) || '—';
}
function fmtAge(dob) {
  if (!dob) return null;
  const d = new Date(dob);
  const t = new Date();
  let age = t.getFullYear() - d.getFullYear();
  if (t.getMonth() < d.getMonth() || (t.getMonth() === d.getMonth() && t.getDate() < d.getDate())) age -= 1;
  return age;
}

export default function EnrollmentDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('belegungen');
  const [abrechnung, setAbrechnung] = useState(null);
  const [abrLoading, setAbrLoading] = useState(false);
  const [year, setYear] = useState(new Date().getFullYear());
  const [allGuardians, setAllGuardians] = useState([]);

  useEffect(() => {
    api.get('/guardians').then((r) => {
      setAllGuardians(Array.isArray(r.data) ? r.data : (r.data.items || []));
    }).catch(() => setAllGuardians([]));
  }, []);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        const r = await api.get(`/enrollments/${id}/full`);
        setData(r.data);
      } catch (err) {
        toast.error(err.response?.data?.detail || 'Belegung nicht gefunden');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [id]);

  useEffect(() => {
    if (tab !== 'abrechnung' || !id) return;
    setAbrLoading(true);
    api.get(`/enrollments/${id}/abrechnung?months=12&start_year=${year}`)
      .then((r) => setAbrechnung(r.data))
      .catch(() => setAbrechnung(null))
      .finally(() => setAbrLoading(false));
  }, [tab, id, year]);

  const studentName = useMemo(() => {
    if (!data?.student) return '';
    return `${data.student.first_name || ''} ${data.student.last_name || ''}`.trim();
  }, [data]);
  const age = useMemo(() => fmtAge(data?.student?.date_of_birth), [data]);

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-96">
          <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
        </div>
      </Layout>
    );
  }
  if (!data) return null;

  return (
    <Layout>
      <div className="p-6 md:p-8 max-w-7xl mx-auto" data-testid="enrollment-detail">
        {/* Header */}
        <div className="flex items-center gap-3 mb-6">
          <div className="flex-1">
            <h1 className="text-2xl font-bold text-slate-900">
              Belegung · {studentName}
            </h1>
            <div className="text-sm text-slate-500 flex items-center gap-2">
              <span>{courseDisplayName(data.course?.name, data.course?.subject_name) || data.course?.name}</span>
              {data.tariff?.name && (
                <>
                  <span className="text-slate-300">·</span>
                  <span className="text-brand-red">{data.tariff.name}</span>
                </>
              )}
              <span className="text-slate-300">·</span>
              <Badge variant="outline" className={
                data.enrollment.status === 'active'
                  ? 'border-emerald-200 text-emerald-700 bg-emerald-50'
                  : 'border-slate-200 text-slate-600 bg-slate-50'
              }>
                {statusLabel(data.enrollment.status)}
              </Badge>
            </div>
          </div>
        </div>

        {/* Tabs */}
        <Tabs value={tab} onValueChange={setTab}>
          <TabsList className="grid grid-cols-4 w-full max-w-2xl mb-6">
            <TabsTrigger value="veranstaltungen" data-testid="tab-veranstaltungen">
              <Calendar className="w-4 h-4 mr-1" /> Veranstaltungen
            </TabsTrigger>
            <TabsTrigger value="belegungen" data-testid="tab-belegungen">
              <Users className="w-4 h-4 mr-1" /> Belegungen
            </TabsTrigger>
            <TabsTrigger value="abrechnung" data-testid="tab-abrechnung">
              <Receipt className="w-4 h-4 mr-1" /> Abrechnung
            </TabsTrigger>
            <TabsTrigger value="uebersicht" data-testid="tab-uebersicht">
              <List className="w-4 h-4 mr-1" /> Übersicht
            </TabsTrigger>
          </TabsList>

          {/* Veranstaltungen */}
          <TabsContent value="veranstaltungen" className="space-y-4">
            <div className="grid md:grid-cols-2 gap-4">
              <Card className="p-5">
                <SectionTitle>Kurs</SectionTitle>
                <Field label="Bezeichnung">{courseDisplayName(data.course?.name, data.course?.subject_name) || data.course?.name}</Field>
                <Field label="Fach">{data.course?.subject_name}</Field>
                <Field label="Typ">{courseTypeLabel(data.course?.course_type)}</Field>
                <Field label="Dauer (min)">{data.course?.duration_minutes}</Field>
                <Field label="Rhythmus">{rhythmLabel(data.course?.rhythm)}</Field>
                <Field label="Max. Teiln.">{data.course?.max_capacity}</Field>
                <Field label="Status">{statusLabel(data.course?.status)}</Field>
              </Card>
              <Card className="p-5">
                <SectionTitle color="amber">Termin & Ort</SectionTitle>
                <Field label="Wochentag">
                  {formatWeekdayShort(data.course?.day_of_week) || '—'}
                </Field>
                <Field label="Uhrzeit">{formatTime(data.course?.start_time) || '—'}</Field>
                <Field label="Beginn">{fmtDate(data.enrollment.start_date)}</Field>
                <Field label="Ende">{fmtDate(data.enrollment.end_date)}</Field>
                <Field label="Unterrichtsort">{data.location?.name}</Field>
                <Field label="Raum">{data.room?.name}</Field>
                <Field label="Areal">{data.location?.region}</Field>
                <Field label="Gkz">{data.location?.municipality_code}</Field>
              </Card>
              <Card className="p-5 md:col-span-2">
                <SectionTitle color="emerald">Lehrkraft</SectionTitle>
                <div className="flex items-center gap-3">
                  <GraduationCap className="w-8 h-8 text-emerald-600" />
                  <div>
                    <div className="font-semibold text-slate-800">{data.teacher?.name || '—'}</div>
                    <div className="text-xs text-slate-500">
                      Dauer: {data.course?.duration_minutes ?? '—'} Min.
                    </div>
                  </div>
                </div>
              </Card>
            </div>
          </TabsContent>

          {/* Belegungen */}
          <TabsContent value="belegungen" className="space-y-4">
            <div className="grid md:grid-cols-2 gap-4">
              <Card className="p-5">
                <SectionTitle>Teilnehmer:in</SectionTitle>
                <Field label="Anrede">{age && age >= 18 ? 'Erwachsene/r' : 'Schüler:in'}</Field>
                <Field label="PK">{data.student?.person_number}</Field>
                <Field label="Name">{studentName}</Field>
                <Field label="Geburtsdatum">
                  {fmtDate(data.student?.date_of_birth)}
                  {age != null && <span className="ml-2 text-xs text-slate-500">({age} J.)</span>}
                </Field>
                <Field label="Status">{statusLabel(data.student?.status)}</Field>
                <Field label="Eintritt">{fmtDate(data.enrollment.start_date)}</Field>
                <Field label="Austritt">{fmtDate(data.enrollment.end_date)}</Field>
                <Field label="Fach">{data.tariff?.name}</Field>
                <Field label="Lehrkraft">{data.teacher?.name}</Field>
                <Field label="Termin">
                  {data.course?.day_of_week != null && data.course?.start_time
                    ? formatWeekdayTimeRange(
                        data.course.day_of_week,
                        data.course.start_time,
                        data.course.duration_minutes
                          ? addMinutesToTime(data.course.start_time, data.course.duration_minutes)
                          : null,
                      )
                    : '—'}
                </Field>
              </Card>
              <Card className="p-5">
                <SectionTitle color="amber">Zahlungspflichtige:r</SectionTitle>
                {data.payer ? (
                  <>
                    <Field label="Kassenz.">{data.family?.imikel_kassenzeichen}</Field>
                    <Field label="Name">{`${data.payer.first_name} ${data.payer.last_name}`.trim()}</Field>
                    <Field label="Adresse">{data.payer.address}</Field>
                    <Field label="PLZ/Ort">
                      {data.payer.postal_code} {data.payer.city}
                    </Field>
                    <Field label="Telefon">{data.payer.phone}</Field>
                    <Field label="E-Mail">{data.payer.email}</Field>
                    <Field label="IBAN">{data.payer.iban}</Field>
                    <Field label="Mandat-Ref.">{data.payer.sepa_mandate_reference}</Field>
                    <Field label="Mandat-Datum">{fmtDate(data.payer.sepa_mandate_date)}</Field>
                  </>
                ) : (
                  <p className="text-sm text-slate-400 italic">Kein:e Zahlungspflichtige:r hinterlegt</p>
                )}
              </Card>
              <Card className="p-5 md:col-span-2">
                <SectionTitle color="brand">Familie</SectionTitle>
                {data.family ? (
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <Users className="w-6 h-6 text-brand-red" />
                      <div>
                        <div className="font-semibold text-slate-800">{data.family.name}</div>
                        {data.family.imikel_kassenzeichen && (
                          <div className="text-xs text-slate-500">
                            Kassenzeichen: {data.family.imikel_kassenzeichen}
                          </div>
                        )}
                      </div>
                    </div>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => navigate(`/families?id=${data.family.id}`)}
                      data-testid="enrollment-family-link"
                    >
                      Familie öffnen →
                    </Button>
                  </div>
                ) : (
                  <p className="text-sm text-slate-400 italic">Keine Familie verknüpft</p>
                )}
              </Card>
              <Card className="p-5 md:col-span-2">
                <MultiPayerEditor enrollmentId={id} allGuardians={allGuardians} />
              </Card>
              <Card className="p-5 md:col-span-2">
                <HistoryPanel enrollmentId={id} />
              </Card>
            </div>
          </TabsContent>

          {/* Abrechnung */}
          <TabsContent value="abrechnung" className="space-y-4">
            <Card className="p-5">
              <div className="flex items-center justify-between mb-3">
                <SectionTitle>Beträge · {year}</SectionTitle>
                <div className="flex items-center gap-1">
                  <Button size="sm" variant="ghost" onClick={() => setYear(year - 1)} data-testid="abr-year-prev">
                    ‹
                  </Button>
                  <span className="text-sm w-14 text-center">{year}</span>
                  <Button size="sm" variant="ghost" onClick={() => setYear(year + 1)} data-testid="abr-year-next">
                    ›
                  </Button>
                </div>
              </div>
              {abrLoading ? (
                <div className="flex items-center justify-center py-12">
                  <Loader2 className="w-5 h-5 animate-spin text-slate-400" />
                </div>
              ) : abrechnung?.months?.length ? (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="text-xs text-slate-500 uppercase">
                      <tr className="border-b border-slate-200">
                        <th className="text-left py-2 pr-3">Monat</th>
                        <th className="text-left py-2 pr-3">Kategorie</th>
                        <th className="text-right py-2 pr-3">Wert</th>
                        <th className="text-right py-2 pr-3">Betrag</th>
                        <th className="text-right py-2 pr-3">Summe (Mo)</th>
                        <th className="text-right py-2">Lfd</th>
                      </tr>
                    </thead>
                    <tbody>
                      {abrechnung.months.map((m, mi) => (
                        m.items.length === 0 ? (
                          <tr key={`m-${mi}`} className="border-b border-slate-100 text-slate-400 italic">
                            <td className="py-2 pr-3 font-medium text-slate-500">{m.month_label}</td>
                            <td className="py-2 pr-3" colSpan={5}>— inaktiv —</td>
                          </tr>
                        ) : m.items.map((it, ii) => (
                          <tr
                            key={`${mi}-${ii}`}
                            className="border-b border-slate-100"
                            data-testid={`abr-row-${mi}-${ii}`}
                          >
                            <td className="py-2 pr-3 font-medium text-slate-700">
                              {ii === 0 ? m.month_label : ''}
                            </td>
                            <td className="py-2 pr-3 text-slate-700">{it.kategorie}</td>
                            <td className="py-2 pr-3 text-right text-slate-500">
                              {it.wert != null ? formatDeNumber(it.wert) : ''}
                            </td>
                            <td className="py-2 pr-3 text-right">
                              {formatDeNumber(it.betrag)}
                            </td>
                            <td className="py-2 pr-3 text-right font-semibold text-slate-800">
                              {ii === m.items.length - 1 ? formatDeNumber(m.month_total) : ''}
                            </td>
                            <td className="py-2 text-right text-slate-400">
                              {ii === m.items.length - 1 ? formatDeNumber(m.cumulative) : ''}
                            </td>
                          </tr>
                        ))
                      ))}
                    </tbody>
                    <tfoot>
                      <tr className="border-t-2 border-slate-300">
                        <td colSpan={4} className="py-2 pr-3 text-right font-bold">Jahres-Summe</td>
                        <td className="py-2 pr-3 text-right font-bold text-brand-red">
                          {fmtMoney(abrechnung.totals.summe)}
                        </td>
                        <td />
                      </tr>
                    </tfoot>
                  </table>
                </div>
              ) : (
                <p className="text-sm text-slate-400 italic">Keine Beträge für diesen Zeitraum</p>
              )}
            </Card>
          </TabsContent>

          {/* Übersicht */}
          <TabsContent value="uebersicht" className="space-y-4">
            <Card className="p-5">
              <SectionTitle>Aktionen</SectionTitle>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                <Button
                  variant="outline"
                  onClick={() => navigate(`/students/${data.student?.id}`)}
                  className="justify-start"
                >
                  <UserIcon className="w-4 h-4 mr-2" /> Schüler-Profil
                </Button>
                <Button
                  variant="outline"
                  onClick={() => navigate(`/courses/${data.course?.id}`)}
                  className="justify-start"
                >
                  <Calendar className="w-4 h-4 mr-2" /> Kurs öffnen
                </Button>
                <Button
                  variant="outline"
                  onClick={() => navigate(`/teachers`)}
                  className="justify-start"
                >
                  <GraduationCap className="w-4 h-4 mr-2" /> Lehrkräfte
                </Button>
                {data.family && (
                  <Button
                    variant="outline"
                    onClick={() => navigate(`/families?id=${data.family.id}`)}
                    className="justify-start"
                  >
                    <Users className="w-4 h-4 mr-2" /> Familie
                  </Button>
                )}
                <Button
                  variant="outline"
                  onClick={() => navigate('/invoices')}
                  className="justify-start"
                >
                  <Receipt className="w-4 h-4 mr-2" /> Rechnungen
                </Button>
                <Button
                  variant="outline"
                  onClick={() => navigate('/calendar?date=today')}
                  className="justify-start"
                >
                  <Clock className="w-4 h-4 mr-2" /> Kalender
                </Button>
              </div>
            </Card>
            <Card className="p-5">
              <SectionTitle color="amber">Konflikte</SectionTitle>
              {data.enrollment.has_conflict ? (
                <div className="flex items-center gap-2 text-amber-700">
                  <Badge className="bg-amber-100 text-amber-800 border-amber-300">
                    Konflikt
                  </Badge>
                  <span className="text-sm">
                    Gruppe: <code className="text-xs">{data.enrollment.conflict_group_key}</code>
                  </span>
                </div>
              ) : (
                <p className="text-sm text-emerald-700">Keine Konflikte erkannt</p>
              )}
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </Layout>
  );
}
